import { useState } from 'react';
import { Button } from '../buttons/Button';
import {
    DIMENSIONS,
    DIMENSION_LABELS,
    PlanHistoryEntry,
} from '../../lib/plan/types';

// Kolik měření ukázat, než si učitel řekne o zbytek.
const COLLAPSED_COUNT = 3;

interface PlanHistoryProps {
    history: PlanHistoryEntry[];
    // Chyba načtení historie — bez ní by se selhání tvářilo jako „zatím žádná
    // historie" a učiteli bychom lhali o tom, že žádná měření nemá.
    error?: boolean;
    onRetry?: () => void;
}

function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString('cs-CZ', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
}

function percent(score: number): number {
    return Math.round((score / 4) * 100);
}

export function PlanHistory({ history, error = false, onRetry }: PlanHistoryProps) {
    const [expanded, setExpanded] = useState(false);

    if (error) {
        return (
            <div className="mt-6 rounded-2xl bg-white p-6 shadow-sm">
                <h4 className="mb-2">Historie</h4>
                <p className="mb-4 text-sm text-ink-800">
                    Historii měření se nepodařilo načíst. Zkuste to prosím
                    znovu.
                </p>
                <Button color="product" onClick={onRetry}>
                    Zkusit znovu
                </Button>
            </div>
        );
    }

    // S jedním měřením není co porovnávat — místo prázdné tabulky pobídka.
    if (history.length < 2) {
        return (
            <div className="mt-6 rounded-2xl bg-white p-6 shadow-sm">
                <h4 className="mb-2">Historie</h4>
                <p className="text-sm text-ink-800">
                    Až plán dokončíte, nabídneme vám nové měření a uvidíte tady
                    svůj posun.
                </p>
            </div>
        );
    }

    const visible = expanded ? history : history.slice(0, COLLAPSED_COUNT);
    const hidden = history.length - visible.length;

    return (
        <div className="mt-6 rounded-2xl bg-white p-6 shadow-sm">
            <h4 className="mb-1">Historie</h4>
            <p className="mb-4 text-sm text-ink-700">
                Každé vyplnění dotazníku zůstává uložené, novější nahoře.
            </p>

            <ol className="space-y-3">
                {visible.map((entry, index) => (
                    <li
                        key={entry.id}
                        className="rounded-xl bg-cloud-100 px-4 py-3"
                    >
                        <p className="mb-2 text-sm font-semibold text-ink-800">
                            {formatDate(entry.created_at)}
                            {index === 0 && (
                                <span className="ml-2 text-xs font-medium text-ink-700">
                                    aktuální
                                </span>
                            )}
                        </p>
                        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-ink-700">
                            {DIMENSIONS.map((dimension) => (
                                <span key={dimension}>
                                    {DIMENSION_LABELS[dimension]}{' '}
                                    <span className="font-medium text-ink-900">
                                        {percent(entry.dimension_scores[dimension])} %
                                    </span>
                                </span>
                            ))}
                            <span>
                                Celkově{' '}
                                <span className="font-medium text-ink-900">
                                    {percent(entry.wellbeing)} %
                                </span>
                            </span>
                        </div>
                    </li>
                ))}
            </ol>

            {hidden > 0 && (
                <button
                    type="button"
                    onClick={() => setExpanded(true)}
                    className="mt-4 text-sm font-medium text-product-600 hover:underline"
                >
                    Zobrazit více ({hidden})
                </button>
            )}
        </div>
    );
}
